import { emojiToTwemoji, getStringType, turndownService, isReadOnly } from "../utils";
import { FieldTypes, KeyboardEvents } from "../types";
import { useRouter } from "next/router";
import { useNotificaitons, usePack, useRunSync, useUploadFiles } from "./index";
import { useCallback, useEffect } from "react";
import { useProgressContext } from "lib/context/progressContext";
import { useCreateAsset } from "./useCreateAsset";

export const useHandlePastedAsset = () => {
  const router = useRouter();
  const { data: pack } = usePack(router.query.id as string);
  const { assetLimitNotification, storageLimitNotification } = useNotificaitons();
  const { uploadFiles, percent } = useUploadFiles(router.query.id as string);
  const { setProgress } = useProgressContext();
  const { sync } = useRunSync(router.query.id as string);
  const { text, html, link } = useCreateAsset();

  useEffect(() => {
    setProgress(percent)
  }, [percent, setProgress]);

  const handlePastedFiles = useCallback(async (files: any) => {
    await uploadFiles(files);
  }, [uploadFiles]);

  const handlePastedAsset = useCallback(async (e: ClipboardEvent): Promise<void> => {
    const target = e.target as Element;

    if (!pack?.id) {
      return;
    }

    if (target.nodeName === 'INPUT' || target.nodeName === 'TEXTAREA') {
      return;
    }

    try {
      if (e.clipboardData.files.length) {
        await handlePastedFiles(e.clipboardData.files);
        return;
      }

      let pastedData = e.clipboardData.getData('text/plain');

      const typeOfAsset = getStringType(pastedData);

      if (typeOfAsset === FieldTypes.TEXT) {
        const html = e.clipboardData.getData('text/html');

        if (html && html.length) {
          try {
            pastedData = turndownService.turndown(html);
          }
          catch(e) {
            console.error(e);
          }
        }

        pastedData = emojiToTwemoji(pastedData).trim();
      }

      if (typeOfAsset === FieldTypes.LINK) {
        link(pastedData);
      }
      else if (typeOfAsset === FieldTypes.HTML) {
        html(pastedData);
      } 
      else {
        text(pastedData);
      }

      await sync();
    } catch (err) {
      console.log(err);
      
      if (err.response?.status === 507) {
        storageLimitNotification();
      }
    }
  }, [assetLimitNotification, handlePastedFiles, sync, pack, storageLimitNotification]);

  useEffect(() => {
    if (isReadOnly(pack)) {
      return;
    }

    if (router.pathname !== `/packs/[id]`) {
      return;
    }

    document.addEventListener(KeyboardEvents.PASTE, handlePastedAsset, false);
    return () => {
      document.removeEventListener(KeyboardEvents.PASTE, handlePastedAsset, false);
    }

  }, [pack, router.pathname, handlePastedAsset]);

  return {
    handlePastedAsset,
  }
}
